<script setup>
import { ref, computed, onMounted, reactive, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';
import CompanyAPI from 'dashboard/api/companies';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import ContactAPI from 'dashboard/api/contacts';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  if (currentSearch) {
    await companiesStore.search({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } else {
    await companiesStore.get({
      page: currentPage,
      sort: currentSort,
    });
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

// ========== CRUD ==========
const showModal = ref(false);
const editingCompany = ref(null);
const isSaving = ref(false);
const companyForm = ref({ name: '', domain: '', description: '' });

function openCreateModal() {
  editingCompany.value = null;
  companyForm.value = { name: '', domain: '', description: '' };
  showModal.value = true;
}

function openEditModal(companyId) {
  const company = companies.value.find(c => c.id === companyId);
  if (!company) return;
  editingCompany.value = company;
  companyForm.value = {
    name: company.name || '',
    domain: company.domain || '',
    description: company.description || '',
  };
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  editingCompany.value = null;
  companyForm.value = { name: '', domain: '', description: '' };
}

async function saveCompany() {
  if (!companyForm.value.name) return;
  isSaving.value = true;
  try {
    if (editingCompany.value) {
      await CompanyAPI.update(editingCompany.value.id, {
        company: companyForm.value,
      });
      useAlert('Empresa atualizada com sucesso.');
    } else {
      await CompanyAPI.create({ company: companyForm.value });
      useAlert('Empresa criada com sucesso.');
    }
    closeModal();
    fetchCompanies();
  } catch (e) {
    useAlert(
      e.response?.data?.message ||
        e.response?.data?.error ||
        'Erro ao salvar empresa.'
    );
  }
  isSaving.value = false;
}

async function deleteCompany(companyId) {
  const company = companies.value.find(c => c.id === companyId);
  if (!company) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir a empresa "${company.name}"?`)) return;
  try {
    await CompanyAPI.delete(companyId);
    useAlert('Empresa excluída com sucesso.');
    fetchCompanies();
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao excluir empresa.');
  }
}

// ========== Contacts cascade ==========
const expandedCompanyId = ref(null);
const companyContacts = ref([]);
const companyContactsMeta = ref({ total_count: 0, current_page: 1 });
const isLoadingContacts = ref(false);
const contactsPage = ref(1);
const selectedContactIds = ref(new Set());
const isDetaching = ref(false);

const hasSelectedContacts = computed(() => selectedContactIds.value.size > 0);
const allContactsSelected = computed(() =>
  companyContacts.value.length > 0 &&
  companyContacts.value.every(c => selectedContactIds.value.has(c.id))
);

function toggleContactSelection(contactId) {
  const s = new Set(selectedContactIds.value);
  if (s.has(contactId)) s.delete(contactId);
  else s.add(contactId);
  selectedContactIds.value = s;
}

function toggleSelectAll() {
  if (allContactsSelected.value) {
    selectedContactIds.value = new Set();
  } else {
    selectedContactIds.value = new Set(companyContacts.value.map(c => c.id));
  }
}

async function detachSelectedContacts() {
  if (!expandedCompanyId.value || selectedContactIds.value.size === 0) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Desvincular ${selectedContactIds.value.size} contato(s) desta empresa?`)) return;
  isDetaching.value = true;
  try {
    await CompanyAPI.detachContacts(expandedCompanyId.value, [...selectedContactIds.value]);
    useAlert(`${selectedContactIds.value.size} contato(s) desvinculado(s).`);
    selectedContactIds.value = new Set();
    fetchCompanyContacts(expandedCompanyId.value);
    fetchCompanies();
  } catch {
    useAlert('Erro ao desvincular contatos.');
  } finally {
    isDetaching.value = false;
  }
}
// ========== Assign contacts ==========
const showAssignModal = ref(false);
const assignSearchQuery = ref('');
const assignSearchResults = ref([]);
const isSearchingContacts = ref(false);
const isAssigning = ref(false);

const onAssignSearch = debounce(async query => {
  assignSearchQuery.value = query;
  if (!query) {
    assignSearchResults.value = [];
    return;
  }
  isSearchingContacts.value = true;
  try {
    const { data } = await ContactAPI.search(query);
    assignSearchResults.value = data.payload || [];
  } catch (e) {
    //
  } finally {
    isSearchingContacts.value = false;
  }
}, 300);

function openAssignModal() {
  assignSearchQuery.value = '';
  assignSearchResults.value = [];
  showAssignModal.value = true;
}

function closeAssignModal() {
  showAssignModal.value = false;
  assignSearchQuery.value = '';
  assignSearchResults.value = [];
}

async function attachContact(contactId) {
  if (!expandedCompanyId.value) return;
  isAssigning.value = true;
  try {
    await CompanyAPI.attachContacts(expandedCompanyId.value, [contactId]);
    useAlert('Contato atribuído com sucesso.');
    closeAssignModal();
    fetchCompanyContacts(expandedCompanyId.value);
    fetchCompanies();
  } catch (e) {
    useAlert('Erro ao atribuir contato.');
  } finally {
    isAssigning.value = false;
  }
}

async function fetchCompanyContacts(companyId, page = 1, append = false) {
  isLoadingContacts.value = true;
  try {
    const { data } = await CompanyAPI.getContacts(companyId, page);
    if (append) {
      companyContacts.value = [...companyContacts.value, ...data.data];
    } else {
      companyContacts.value = data.data || [];
    }
    companyContactsMeta.value = data.meta || { total_count: 0, current_page: 1 };
    contactsPage.value = page;
  } catch {
    companyContacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
}

function toggleCompanyContacts(companyId) {
  if (expandedCompanyId.value === companyId) {
    expandedCompanyId.value = null;
    companyContacts.value = [];
    selectedContactIds.value = new Set();
    return;
  }
  expandedCompanyId.value = companyId;
  selectedContactIds.value = new Set();
  fetchCompanyContacts(companyId);
}

function loadMoreContacts() {
  if (!expandedCompanyId.value) return;
  fetchCompanyContacts(expandedCompanyId.value, contactsPage.value + 1, true);
}

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
  >
    <template #header-actions>
      <button
        class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-xl hover:bg-woot-600 transition-colors flex-shrink-0"
        @click="openCreateModal"
      >
        <span class="i-lucide-plus size-4" />
        Nova Empresa
      </button>
    </template>

    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{
        t('COMPANIES.LOADING')
      }}</span>
    </div>
    <div
      v-else-if="companies.length === 0"
      class="flex flex-col items-center justify-center p-16 text-n-slate-10"
    >
      <span class="i-lucide-building-2 size-12 mb-4 opacity-30" />
      <span class="text-base font-medium">{{
        t('COMPANIES.EMPTY_STATE.TITLE')
      }}</span>
      <span class="text-sm mt-1">
        Cadastre empresas para organizar seus contatos e negocios.
      </span>
    </div>
    <div v-else class="flex flex-col gap-3">
      <div v-for="company in companies" :key="company.id">
        <CompaniesCard
          :id="company.id"
          :name="company.name"
          :domain="company.domain"
          :contacts-count="company.contactsCount || 0"
          :description="company.description"
          :avatar-url="company.avatarUrl"
          :updated-at="company.updatedAt"
          @show-company="toggleCompanyContacts"
          @edit-company="openEditModal"
          @delete-company="deleteCompany"
        >
          <template #cascade>
            <div
              v-show="expandedCompanyId === company.id"
              class="border-t border-n-weak bg-n-alpha-1 px-6 py-4 mt-2"
            >
          <div v-if="isLoadingContacts" class="flex items-center gap-2 py-4 pl-2">
            <span class="i-lucide-loader-2 size-4 text-woot-500 animate-spin" />
            <span class="text-xs text-n-slate-10">Carregando contatos...</span>
          </div>
          <template v-else>
            <div v-if="companyContacts.length === 0" class="flex flex-col items-start gap-2 py-4 pl-2">
              <div class="flex items-center gap-2">
                <span class="i-lucide-user-x size-4 text-n-slate-8" />
                <span class="text-xs text-n-slate-10">Nenhum contato vinculado a esta empresa.</span>
              </div>
              <button
                type="button"
                class="flex items-center gap-1 mt-1 px-2.5 py-1.5 text-xs font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/40 transition-colors"
                @click="openAssignModal"
              >
                <span class="i-lucide-user-plus size-3.5" />
                Atribuir Contato
              </button>
            </div>
            <div v-else class="flex flex-col gap-1">
              <div class="flex items-center justify-between px-2 py-1">
                <div class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    class="size-3.5 rounded accent-woot-500 cursor-pointer"
                    :checked="allContactsSelected"
                    @change="toggleSelectAll"
                  />
                  <span class="text-[10px] font-bold text-n-slate-9 uppercase tracking-wider">
                    {{ companyContactsMeta.total_count }} contato(s)
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    v-if="hasSelectedContacts"
                    type="button"
                    class="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-ruby-600 dark:text-ruby-400 bg-ruby-50 dark:bg-ruby-900/20 rounded-lg hover:bg-ruby-100 dark:hover:bg-ruby-900/40 transition-colors"
                    :disabled="isDetaching"
                    @click="detachSelectedContacts"
                  >
                    <span class="i-lucide-unlink size-3" />
                    Desvincular ({{ selectedContactIds.size }})
                  </button>
                  <button
                    type="button"
                    class="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/40 transition-colors"
                    @click="openAssignModal"
                  >
                    <span class="i-lucide-user-plus size-3" />
                    Atribuir Contato
                  </button>
                </div>
              </div>
              <div
                v-for="contact in companyContacts" :key="contact.id"
                class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-2 transition-colors group"
              >
                <div
                  class="relative w-8 h-8 rounded-full bg-woot-100 dark:bg-woot-900/30 flex items-center justify-center text-xs font-bold text-woot-600 flex-shrink-0 overflow-hidden cursor-pointer"
                  @click.stop="toggleContactSelection(contact.id)"
                >
                  <template v-if="selectedContactIds.has(contact.id)">
                    <div class="absolute inset-0 bg-woot-500 flex items-center justify-center">
                      <span class="i-lucide-check size-4 text-white" />
                    </div>
                  </template>
                  <template v-else>
                    <img v-if="contact.thumbnail" :src="contact.thumbnail" class="w-full h-full object-cover" />
                    <span v-else>{{ (contact.name || '?')[0].toUpperCase() }}</span>
                    <div class="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <span class="i-lucide-square size-3.5 text-white" />
                    </div>
                  </template>
                </div>
                <router-link
                  :to="{ name: 'contacts_edit', params: { contactId: contact.id } }"
                  class="flex-1 min-w-0"
                >
                  <p class="text-sm font-medium text-n-slate-12 truncate">{{ contact.name || 'Sem nome' }}</p>
                  <div class="flex items-center gap-2 text-[11px] text-n-slate-10">
                    <span v-if="contact.email" class="truncate">{{ contact.email }}</span>
                    <span v-if="contact.email && contact.phone_number" class="text-n-slate-6">·</span>
                    <span v-if="contact.phone_number">{{ contact.phone_number }}</span>
                  </div>
                </router-link>
                <span class="i-lucide-chevron-right size-3.5 text-n-slate-8 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              <!-- Load more -->
              <button
                v-if="companyContacts.length < companyContactsMeta.total_count"
                type="button"
                class="flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-woot-600 hover:text-woot-700 cursor-pointer transition-colors"
                @click="loadMoreContacts"
              >
                <span class="i-lucide-chevron-down size-3" />
                Carregar mais
              </button>
            </div>
          </template>
        </div>
        </template>
      </CompaniesCard>
      </div>
    </div>
  </CompaniesListLayout>

  <!-- Create/Edit Modal -->
  <Teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center"
    >
      <div
        class="absolute inset-0 bg-black/40 backdrop-blur-sm"
        @click="closeModal"
      />
      <div
        class="relative w-full max-w-lg mx-4 bg-white dark:bg-n-solid-2 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div class="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ editingCompany ? 'Editar Empresa' : 'Nova Empresa' }}
          </h2>
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 transition-colors"
            @click="closeModal"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="px-6 pb-6 flex flex-col gap-4">
          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1.5 block">
              Nome *
            </label>
            <input
              v-model="companyForm.name"
              class="w-full px-3.5 py-2.5 border border-n-weak rounded-xl text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500 transition-all placeholder:text-n-slate-9"
              placeholder="Nome da empresa"
              @keydown.enter="saveCompany"
            />
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1.5 block">
              Dominio
            </label>
            <input
              v-model="companyForm.domain"
              class="w-full px-3.5 py-2.5 border border-n-weak rounded-xl text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500 transition-all placeholder:text-n-slate-9"
              placeholder="empresa.com.br"
              @keydown.enter="saveCompany"
            />
          </div>
          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1.5 block">
              Descricao
            </label>
            <textarea
              v-model="companyForm.description"
              class="w-full px-3.5 py-2.5 border border-n-weak rounded-xl text-sm bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500 transition-all resize-none placeholder:text-n-slate-9"
              rows="3"
              placeholder="Descricao da empresa (opcional)"
            />
          </div>
          <div class="flex items-center justify-end gap-3 pt-2">
            <button
              class="px-4 py-2.5 text-sm font-medium text-n-slate-11 rounded-xl hover:bg-n-alpha-2 transition-colors"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="px-5 py-2.5 text-sm font-medium text-white bg-woot-500 rounded-xl hover:bg-woot-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!companyForm.name || isSaving"
              @click="saveCompany"
            >
              {{
                isSaving
                  ? 'Salvando...'
                  : editingCompany
                    ? 'Salvar'
                    : 'Criar Empresa'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Assign Contact Modal -->
  <Teleport to="body">
    <div
      v-if="showAssignModal"
      class="fixed inset-0 z-[9999] flex items-center justify-center"
    >
      <div
        class="absolute inset-0 bg-black/40 backdrop-blur-sm"
        @click="closeAssignModal"
      />
      <div
        class="relative w-full max-w-lg mx-4 bg-white dark:bg-n-solid-2 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div class="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 class="text-lg font-semibold text-n-slate-12">
            Atribuir Contato
          </h2>
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 transition-colors"
            @click="closeAssignModal"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="px-6 pb-6">
          <div class="relative mb-4">
            <span class="absolute left-3 top-2.5 i-lucide-search size-4 text-n-slate-10" />
            <input
              type="text"
              class="w-full pl-9 pr-3 py-2 rounded-xl border border-n-weak bg-n-background text-sm text-n-slate-12 outline-none focus:border-woot-500 transition-colors"
              placeholder="Buscar por nome, e-mail ou telefone..."
              @input="e => onAssignSearch(e.target.value)"
            />
          </div>

          <div class="max-h-64 overflow-y-auto">
            <div v-if="isSearchingContacts" class="flex justify-center py-4">
              <span class="i-lucide-loader-2 size-5 text-woot-500 animate-spin" />
            </div>
            <div v-else-if="assignSearchQuery && assignSearchResults.length === 0" class="text-center py-4 text-sm text-n-slate-10">
              Nenhum contato encontrado.
            </div>
            <div v-else-if="assignSearchResults.length > 0" class="flex flex-col gap-1">
              <div
                v-for="contact in assignSearchResults" :key="contact.id"
                class="flex items-center gap-3 p-2 rounded-lg hover:bg-n-alpha-2 transition-colors cursor-pointer"
                @click="attachContact(contact.id)"
              >
                <div class="size-8 rounded-full bg-woot-100 flex items-center justify-center text-xs font-bold text-woot-600 overflow-hidden flex-shrink-0">
                  <img v-if="contact.thumbnail" :src="contact.thumbnail" class="w-full h-full object-cover" />
                  <span v-else>{{ (contact.name || '?')[0].toUpperCase() }}</span>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">{{ contact.name || 'Sem nome' }}</p>
                  <p v-if="contact.email || contact.phone_number" class="text-xs text-n-slate-10 truncate">
                    {{ contact.email }} {{ contact.phone_number ? `· ${contact.phone_number}` : '' }}
                  </p>
                </div>
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-medium text-woot-600 bg-woot-50 hover:bg-woot-100 rounded-lg transition-colors flex-shrink-0"
                  :disabled="isAssigning"
                >
                  Atribuir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
